// Per-transaction attachment panel (CR.g) — list + upload + download, wiring the
// TRES Phase 4.c TransactionAttachment endpoints (`/ops/transactions/{id}/
// attachments[/{aid}]`). Manager / linked-client can upload (access is enforced
// server-side); cross-tenant is invisible. Self-loads on `txId`; an upload refetches.
// Mirrors the Documents.tsx UploadRow FormData+CSRF pattern.
import { useEffect, useRef, useState } from 'react';

import { api } from '../lib/api';

interface AttachmentRow {
  id: string;
  caption: string;
  filename: string;
  byte_size: number;
  uploaded_by_email: string;
  uploaded_at: string;
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m && m[2] ? decodeURIComponent(m[2]) : null;
}

const fmtSize = (n: number) =>
  n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US',
  { year: 'numeric', month: 'short', day: 'numeric' });

export function TxAttachments({ txId, txRef, onClose }: {
  txId: string; txRef?: string; onClose: () => void;
}) {
  const [rows, setRows] = useState<AttachmentRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // The Activity table's clean-panel is tall, so the freshly-opened panel can sit
  // below the fold — scroll it into view on open (parent remounts per tx via key).
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    let cancelled = false; setLoadErr(null);
    api<AttachmentRow[]>(`/ops/transactions/${txId}/attachments`)
      .then(r => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setLoadErr('Failed to load attachments.'); });
    return () => { cancelled = true; };
  }, [txId, reloadKey]);

  const upload = async () => {
    if (!file || busy) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    if (caption.trim()) fd.append('caption', caption.trim());
    try {
      const csrf = getCookie('csrftoken');
      const res = await fetch(`/api/v1/ops/transactions/${txId}/attachments`, {
        method: 'POST', credentials: 'include',
        headers: csrf ? { 'X-CSRFToken': csrf } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('upload failed');
      setFile(null); setCaption(''); setMsg('Uploaded.'); setReloadKey(k => k + 1);
    } catch { setMsg('Upload failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div ref={rootRef} style={{ marginTop: 24, borderTop: '1px solid var(--ink)', paddingTop: 18, maxWidth: 640 }}>
      <div className="kicker-row">
        <span className="kicker">DOCUMENTS{txRef ? ` · ${txRef}` : ''}</span>
        <button type="button" className="btn-link" onClick={onClose}>Close</button>
      </div>
      {loadErr && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{loadErr}</p>}

      <table className="tbl" style={{ marginTop: 12 }}>
        <thead>
          <tr><th>FILE</th><th>CAPTION</th><th data-align="right">SIZE</th><th data-align="right">UPLOADED</th></tr>
        </thead>
        <tbody>
          {(rows || []).map(a => (
            <tr key={a.id}>
              <td>
                <a className="btn-link" href={`/api/v1/ops/transactions/${txId}/attachments/${a.id}`}
                   download style={{ textDecoration: 'none' }}>{a.filename || 'download'} ↓</a>
              </td>
              <td>{a.caption || '—'}</td>
              <td data-align="right" className="num">{fmtSize(a.byte_size)}</td>
              <td data-align="right" className="num">{fmtDate(a.uploaded_at)}</td>
            </tr>
          ))}
          {rows && !rows.length && (
            <tr><td colSpan={4} className="label-s" style={{ color: 'var(--ink-3)' }}>
              No documents attached to this transaction yet.
            </td></tr>
          )}
        </tbody>
      </table>

      <div style={{ border: 'var(--hairline)', borderColor: 'var(--rule)', padding: 16, marginTop: 16,
                    display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="label" style={{ marginRight: 8 }}>ATTACH</span>
        <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
        <input className="field-input" placeholder="Caption (optional)" value={caption}
               onChange={e => setCaption(e.target.value)} style={{ maxWidth: 240 }} />
        <button type="button" className="btn" disabled={busy || !file} onClick={upload}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
        {msg && <span className="label-s" style={{ color: msg === 'Uploaded.' ? 'var(--ink-2)' : 'var(--oxblood)' }}>{msg}</span>}
      </div>
    </div>
  );
}
